import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addRequestComment,
  createPublicServiceRequest,
  createServiceRequest,
  getServiceRequest,
  listServiceRequests,
  updateRequestStatus,
  uploadPublicRequestAttachment,
  uploadRequestAttachment,
  type CreatePublicServiceRequestInput,
  type CreateServiceRequestInput,
  type ListRequestsFilters,
} from '@/lib/requests'
import type { ServiceRequestStatus } from '@riverpark/types'

export function useServiceRequests(filters: ListRequestsFilters = {}) {
  return useQuery({
    queryKey: ['service-requests', filters],
    queryFn: () => listServiceRequests(filters),
  })
}

export function useServiceRequest(id: string | null) {
  return useQuery({
    queryKey: ['service-requests', id],
    queryFn: () => getServiceRequest(id!),
    enabled: id !== null,
  })
}

export function useCreateServiceRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateServiceRequestInput) => createServiceRequest(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-requests'] }),
  })
}

/** No query invalidation — the caller is anonymous and has no request list of their own to
 * refresh. */
export function useSubmitPublicServiceRequest() {
  return useMutation({
    mutationFn: (input: CreatePublicServiceRequestInput) => createPublicServiceRequest(input),
  })
}

export function useAddRequestComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: string }) =>
      addRequestComment(requestId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['service-requests', variables.requestId] })
    },
  })
}

export function useUpdateRequestStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, status }: { requestId: string; status: ServiceRequestStatus }) =>
      updateRequestStatus(requestId, status),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['service-requests', variables.requestId] })
      qc.invalidateQueries({ queryKey: ['service-requests'] })
    },
  })
}

export function useUploadRequestAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ requestId, file }: { requestId: string; file: File }) =>
      uploadRequestAttachment(requestId, file),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['service-requests', variables.requestId] })
    },
  })
}

/** No query invalidation — same reasoning as useSubmitPublicServiceRequest. */
export function useUploadPublicRequestAttachment() {
  return useMutation({
    mutationFn: ({ requestId, file }: { requestId: string; file: File }) =>
      uploadPublicRequestAttachment(requestId, file),
  })
}
